import {
  Box,
  Flex,
  Heading,
  Icon,
  Input,
  NumberInput,
  NumberInputField,
  Text,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import {
  RiAddCircleLine,
  RiIndeterminateCircleLine,
} from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import MainModel from "../../../../components/Models/main-model";
import { closeVideoChaptersModel } from "../../../../modules/admin-modules/models/Actions";
import {
  getVideoVdoChaptersRequest,
  updateVideoVdoChaptersRequest,
} from "../../../../modules/admin-modules/videos/Actions";

const secondsToHms = (total = 0) => {
  const t = Math.max(0, parseInt(total, 10) || 0);
  return {
    h: Math.floor(t / 3600),
    m: Math.floor((t % 3600) / 60),
    s: t % 60,
  };
};

const hmsToSeconds = ({ h = 0, m = 0, s = 0 }) =>
  (parseInt(h, 10) || 0) * 3600 +
  (parseInt(m, 10) || 0) * 60 +
  (parseInt(s, 10) || 0);

const defaultRow = (title = "", startTime = 0) => {
  const { h, m, s } = secondsToHms(startTime);
  return { title, h, m, s };
};

const emptyRows = () => [defaultRow(), defaultRow()];

const chaptersToRows = (chapters) => {
  if (!chapters?.length) {
    return emptyRows();
  }
  const sorted = [...chapters].sort(
    (a, b) => (a.startTime ?? 0) - (b.startTime ?? 0)
  );
  const rows = sorted.map((ch, idx) =>
    defaultRow(ch.title ?? "", idx === 0 ? 0 : ch.startTime ?? 0)
  );
  while (rows.length < 2) {
    rows.push(defaultRow());
  }
  return rows;
};

const validateRows = (rows) => {
  if (rows.length < 2) {
    return "At least 2 chapters are required";
  }
  for (let i = 0; i < rows.length; i++) {
    if (!rows[i].title?.trim()) {
      return `Chapter ${i + 1} title is required`;
    }
  }
  const startTimes = rows.map((r) => hmsToSeconds(r));
  if (startTimes[0] !== 0) {
    return "The first chapter must start at 00:00:00";
  }
  const unique = new Set(startTimes);
  if (unique.size !== startTimes.length) {
    return "Each chapter must have a unique start time";
  }
  const sorted = [...startTimes].sort((a, b) => a - b);
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] - sorted[i - 1] < 10) {
      return "Each chapter must be at least 10 seconds after the previous one";
    }
  }
  return null;
};

const VideoChaptersModal = ({ data }) => {
  const [rows, setRows] = useState(emptyRows());
  const openModal = useSelector((state) => state.model.isOpen43);
  const vdoChapters = useSelector((state) => state.videos.vdoChapters);
  const isLoading = useSelector((state) => state.videos.isLoading);
  const dispatch = useDispatch();

  const closeFunc = () => {
    dispatch(closeVideoChaptersModel());
  };

  useEffect(() => {
    if (openModal && data?.id) {
      dispatch(getVideoVdoChaptersRequest(data.id));
    }
  }, [openModal, data?.id, dispatch]);

  useEffect(() => {
    if (openModal) {
      setRows(chaptersToRows(vdoChapters));
    }
  }, [vdoChapters, openModal]);

  const updateRow = (index, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addRow = () => {
    setRows((prev) => [...prev, defaultRow()]);
  };

  const removeRow = (index) => {
    if (index === 0 || rows.length <= 2) return;
    setRows((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSubmit = () => {
    const error = validateRows(rows);
    if (error) {
      toast.error(error);
      return;
    }
    const chapters = rows
      .map((r) => ({
        title: r.title.trim(),
        startTime: hmsToSeconds(r),
      }))
      .sort((a, b) => a.startTime - b.startTime);

    dispatch(
      updateVideoVdoChaptersRequest(
        { id: data.id, body: { chapters } },
        closeFunc
      )
    );
  };

  const header = (
    <Box>
      <Heading textAlign="center" color="main_1" fontSize="24px">
        Video Chapters
      </Heading>
      {data?.title && (
        <Text textAlign="center" fontSize="sm" color="gray.600" mt={1}>
          {data.title}
        </Text>
      )}
    </Box>
  );

  return (
    <MainModel
      header={header}
      closeFunc={closeFunc}
      openVar={openModal}
      buttonTitle="Save Chapters"
      buttonFunc={handleSubmit}
      isLoading={isLoading}
    >
      <Flex direction="column" gap={3}>
        <Flex fontWeight="bold" gap={2} align="center" px={1}>
          <Box flex={1}>Title</Box>
          <Box w="70px" textAlign="center">
            H
          </Box>
          <Box w="70px" textAlign="center">
            M
          </Box>
          <Box w="70px" textAlign="center">
            S
          </Box>
          <Box w="60px" />
        </Flex>

        {rows.map((row, index) => {
          const isFirst = index === 0;
          const canRemove = index > 0 && rows.length > 2;

          return (
            <Flex key={index} gap={2} align="center">
              <Input
                flex={1}
                placeholder="Chapter title"
                value={row.title}
                onChange={(e) => updateRow(index, "title", e.target.value)}
              />
              <NumberInput
                w="70px"
                min={0}
                value={row.h}
                isDisabled={isFirst}
                onChange={(_, val) => updateRow(index, "h", val)}
              >
                <NumberInputField />
              </NumberInput>
              <NumberInput
                w="70px"
                min={0}
                max={59}
                value={row.m}
                isDisabled={isFirst}
                onChange={(_, val) => updateRow(index, "m", val)}
              >
                <NumberInputField />
              </NumberInput>
              <NumberInput
                w="70px"
                min={0}
                max={59}
                value={row.s}
                isDisabled={isFirst}
                onChange={(_, val) => updateRow(index, "s", val)}
              >
                <NumberInputField />
              </NumberInput>
              <Flex w="60px" justify="center" gap={1}>
                {index === rows.length - 1 && (
                  <Icon
                    as={RiAddCircleLine}
                    color="main_2"
                    fontSize="20px"
                    cursor="pointer"
                    onClick={addRow}
                  />
                )}
                {canRemove && (
                  <Icon
                    as={RiIndeterminateCircleLine}
                    color="main_2"
                    fontSize="22px"
                    cursor="pointer"
                    onClick={() => removeRow(index)}
                  />
                )}
              </Flex>
            </Flex>
          );
        })}
      </Flex>
    </MainModel>
  );
};

export default VideoChaptersModal;
